import { useState } from 'react';
import { ArrowLeft, Footprints, TrendingUp, Calendar, Target, History, Timer, Flame } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, StepEntryDto } from '../services/health-data.service';
import { activitiesService } from '../services/activities.service';
import { ACTIVITY_TYPES } from '../lib/activityTypes';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface DailyStepsTrackerProps {
  onBack: () => void;
  onNavigate?: (page: string) => void;
}

export function DailyStepsTracker({ onBack, onNavigate }: DailyStepsTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [steps, setSteps] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch step entries
  const {
    data: stepEntriesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['step-entries', user?.id],
    queryFn: () => healthDataService.getStepEntries({ limit: 30 }),
    enabled: !!user,
  });

  const stepEntries = stepEntriesData?.data || stepEntriesData || [];

  // Fetch activities
  const { data: activitiesData } = useQuery({
    queryKey: ['activities', user?.id],
    queryFn: () => activitiesService.list({ limit: 30 }),
    enabled: !!user,
  });
  const activities = Array.isArray(activitiesData) ? activitiesData : [];

  // Create step entry mutation
  const createMutation = useMutation({
    mutationFn: (data: StepEntryDto) => healthDataService.createStepEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['step-entries'] });
      toast.success('Steps logged successfully!');
      setSteps('');
      setSelectedDate(new Date().toISOString().split('T')[0]);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  // Create activity mutation
  const [activityType, setActivityType] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [activityDate, setActivityDate] = useState(new Date().toISOString().split('T')[0]);
  const [distanceKm, setDistanceKm] = useState('');
  const [activityCategoryFilter, setActivityCategoryFilter] = useState<string>('');

  const createActivityMutation = useMutation({
    mutationFn: (data: { activityType: string; durationMinutes: number; date: string; caloriesBurnt?: number; distanceKm?: number }) =>
      activitiesService.create({
        activityType: data.activityType,
        durationMinutes: data.durationMinutes,
        date: data.date,
        caloriesBurnt: data.caloriesBurnt,
        distanceKm: data.distanceKm,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      toast.success('Activity logged successfully!');
      setActivityType('');
      setDurationMinutes('');
      setActivityDate(new Date().toISOString().split('T')[0]);
      setDistanceKm('');
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleActivitySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mins = parseInt(durationMinutes, 10);
    if (!activityType || isNaN(mins) || mins < 1) {
      toast.error('Please select an activity and enter duration (min 1 min)');
      return;
    }
    const typeInfo = ACTIVITY_TYPES.find((t) => t.id === activityType);
    const calories = typeInfo ? Math.round(typeInfo.caloriesPerMinute * mins) : undefined;
    const dist = distanceKm ? parseFloat(distanceKm) : undefined;
    createActivityMutation.mutate({
      activityType,
      durationMinutes: mins,
      date: activityDate,
      caloriesBurnt: calories,
      distanceKm: dist,
    });
  };

  const filteredActivityTypes = activityCategoryFilter
    ? ACTIVITY_TYPES.filter((t) => t.category === activityCategoryFilter)
    : ACTIVITY_TYPES;
  const categories = [...new Set(ACTIVITY_TYPES.map((t) => t.category).filter(Boolean))] as string[];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const stepsNum = parseInt(steps, 10);
    if (isNaN(stepsNum) || stepsNum < 0) {
      toast.error('Please enter a valid number of steps');
      return;
    }

    createMutation.mutate({
      steps: stepsNum,
      date: selectedDate,
    });
  };

  const todaySteps = stepEntries.find((entry: any) => {
    const entryDate = new Date(entry.date || entry.createdAt).toISOString().split('T')[0];
    return entryDate === new Date().toISOString().split('T')[0];
  });

  const totalSteps = stepEntries.reduce((sum: number, entry: any) => sum + (entry.steps || 0), 0);
  const avgSteps =
    stepEntries.length > 0
      ? Math.round(totalSteps / stepEntries.length)
      : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <LoadingSkeleton variant="rectangular" height="2rem" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <LoadingSkeleton variant="rectangular" height="200px" className="mb-8" />
        </div>
      </div>
    );
  }

  if (error) {
    toast.error(handleApiError(error));
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Daily Steps Tracker</h1>
            {onNavigate && (
              <button
                onClick={() => onNavigate('historical-data')}
                className="flex items-center gap-2 px-4 py-2 text-dropsugar-green hover:bg-dropsugar-green/10 rounded-xl font-medium transition-colors border border-dropsugar-green/30"
              >
                <History className="w-5 h-5" />
                View History
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
              <Footprints className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Track Your Daily Steps</h2>
              <p className="text-green-100">Log your steps to monitor your daily activity levels</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-xl">
                <Footprints className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Today's Steps</p>
            <p className="text-3xl font-bold text-gray-900">{todaySteps?.steps || 0}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-dropsugar-green/10 rounded-xl">
                <TrendingUp className="w-6 h-6 text-dropsugar-green" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Average Steps</p>
            <p className="text-3xl font-bold text-gray-900">{avgSteps}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-50 rounded-xl">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">Total Logged</p>
            <p className="text-3xl font-bold text-gray-900">{stepEntries.length}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Log Steps</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  id="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="steps" className="block text-sm font-medium text-gray-700 mb-2">
                Steps
              </label>
              <div className="relative">
                <Footprints className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  id="steps"
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  placeholder="Enter number of steps"
                  min="0"
                  max="100000"
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={createMutation.isPending}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 rounded-xl shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {createMutation.isPending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Logging...</span>
                </>
              ) : (
                <>
                  <Footprints className="w-5 h-5" />
                  <span>Log Steps</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Activity Tracker */}
        <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Timer className="w-6 h-6 text-orange-500" />
            Log Activity (80+ types)
          </h3>
          <form onSubmit={handleActivitySubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={activityCategoryFilter}
                onChange={(e) => setActivityCategoryFilter(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
              <select
                value={activityType}
                onChange={(e) => setActivityType(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900"
              >
                <option value="">Select activity</option>
                {filteredActivityTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.icon} {t.name} ({t.caloriesPerMinute} cal/min)
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={activityDate}
                  onChange={(e) => setActivityDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  placeholder="e.g. 30"
                  min="1"
                  max="600"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900"
                />
              </div>
            </div>
            {activityType && ACTIVITY_TYPES.find((t) => t.id === activityType)?.trackDistance && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Distance (km, optional)</label>
                <input
                  type="number"
                  step="0.1"
                  value={distanceKm}
                  onChange={(e) => setDistanceKm(e.target.value)}
                  placeholder="e.g. 5"
                  min="0"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900"
                />
              </div>
            )}
            <button
              type="submit"
              disabled={createActivityMutation.isPending}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-semibold py-3 rounded-xl shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {createActivityMutation.isPending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Logging...</span>
                </>
              ) : (
                <>
                  <Flame className="w-5 h-5" />
                  <span>Log Activity</span>
                </>
              )}
            </button>
          </form>
        </div>

        {activities.length > 0 && (
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Recent Activities</h3>
            <div className="space-y-4">
              {activities.slice(0, 10).map((entry: any) => {
                const typeInfo = ACTIVITY_TYPES.find((t) => t.id === entry.activityType);
                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-lg ${typeInfo?.bgColor || 'bg-orange-50'}`}>
                        <span className="text-2xl">{typeInfo?.icon || '🏃'}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{typeInfo?.name || entry.activityType}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(entry.date || entry.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })} · {entry.durationMinutes} min
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">{entry.caloriesBurnt ?? '—'} cal</p>
                      {entry.distanceKm && (
                        <p className="text-sm text-gray-600">{entry.distanceKm} km</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {stepEntries.length > 0 && (
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Recent Entries</h3>
            <div className="space-y-4">
              {stepEntries.slice(0, 10).map((entry: any) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Footprints className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {new Date(entry.date || entry.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{entry.steps}</p>
                    <p className="text-sm text-gray-600">steps</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
